import { useCallback, useEffect, useState } from 'react';
import { mensagemDoErro } from '../api';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { FicouNaFila } from '../fila';
import {
  conferirLeitura, instrumentosPendentes, lancarLeitura,
  situacaoDoInstrumento, verInstrumento, verInstrumentos,
  type Instrumento, type Leitura, type Pendencia,
} from '../api/instrumentos';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Instrumentação e monitoramento.
 *
 * ── O que esta tela mostra ──────────────────────────────────────────
 *
 * O parque instalado e a série de cada instrumento. É o serviço mais
 * longo que a Testesolo vende — o piezômetro fica no chão e produz uma
 * leitura por mês durante anos —, e por isso a lista NÃO é filtrada por
 * OS por padrão: o instrumento de 2024 e o de 2026 estão no mesmo talude,
 * e separá-los por campanha esconderia metade do que se foi ver.
 *
 * ── Por que o formulário de leitura muda ────────────────────────────
 *
 * Porque a grandeza muda. Um piezômetro entrega PROFUNDIDADE do nível a
 * partir da boca; um marco superficial entrega COTA de nivelamento. Um
 * campo genérico «valor» aceitaria 3,4 tanto para um quanto para o
 * outro — e 3,4 m de profundidade num marco é uma cota do centro da
 * Terra. Quem recusa a mistura é o banco (`0143`); a tela só não oferece
 * o campo errado.
 *
 * ── O que esta tela NÃO faz ─────────────────────────────────────────
 *
 * **Não alerta.** Não há cor de perigo, nem limiar, nem seta vermelha.
 * Quanto de recalque é demais é critério da Testesolo, e enquanto ela não
 * disser, um alerta aqui seria um número inventado com aparência de
 * engenharia (§25).
 *
 * **Não lê inclinômetro.** O perfil dele não é um número, e o
 * processamento depende de critérios que ninguém definiu. O tipo existe
 * no catálogo, a leitura recusa, e a recusa explica por quê.
 */

const TOM: Record<string, Tom> = {
  ativo: 'ok', danificado: 'critico', obstruido: 'atencao',
  perdido: 'critico', desativado: 'neutro', removido: 'neutro',
  lancada: 'atencao', conferida: 'ok', rejeitada: 'critico',
};

const ROTULO: Record<string, string> = {
  ativo: 'Ativo', danificado: 'Danificado', obstruido: 'Obstruído',
  perdido: 'Perdido', desativado: 'Desativado', removido: 'Removido',
  lancada: 'Lançada', conferida: 'Conferida', rejeitada: 'Rejeitada',
  nivel: 'Nível d’água', recalque: 'Recalque', perfil: 'Perfil',
  outro: 'Outro',
};

const rotulo = (v: string | null) => (v ? ROTULO[v] ?? v : '—');

/** Número do banco (texto) no padrão brasileiro, ou travessão. */
const num = (v: string | null, casas = 3) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR',
    { minimumFractionDigits: casas, maximumFractionDigits: casas });

const dia = (v: string | null) =>
  v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—';

function SituacaoDoInstrumento({ i }: { i: Instrumento }) {
  return (
    <>
      <Selo tom={TOM[i.situacao] ?? 'neutro'} titulo={i.motivo_situacao ?? undefined}>
        {rotulo(i.situacao)}
      </Selo>
      {i.leituras_a_conferir > 0 && (
        <Selo tom="atencao">{i.leituras_a_conferir} a conferir</Selo>
      )}
    </>
  );
}

/** As colunas do parque instalado (onda 219). */
function colunasDoInstrumento(verSerie: (id: string) => void): ColunaComFicha<Instrumento>[] {
  return [
    {
      chave: 'instrumento',
      cabeca: <th scope="col">Instrumento</th>,
      celula: (i) => (
        <th scope="row">
          <strong>{i.codigo}</strong>
          <small className="det"> {i.ponto_codigo}</small>
        </th>
      ),
    },
    { chave: 'tipo', cabeca: <th scope="col">Tipo</th>, celula: (i) => <td>{i.tipo_nome}</td> },
    {
      chave: 'obra', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Obra</th>,
      celula: (i) => <td className="col-opcional">{i.obra_nome ?? '—'}</td>,
    },
    {
      chave: 'instalado', detalhe: true,
      cabeca: <th scope="col" className="col-opcional">Instalado</th>,
      celula: (i) => <td className="col-opcional">{dia(i.data_instalacao)}</td>,
    },
    {
      chave: 'leituras', detalhe: true,
      cabeca: <th scope="col" className="num col-opcional">Leituras</th>,
      celula: (i) => <td className="num col-opcional">{i.leituras}</td>,
    },
    { chave: 'ultima', cabeca: <th scope="col">Última</th>, celula: (i) => <td>{dia(i.ultima_leitura)}</td> },
    {
      chave: 'situacao',
      cabeca: <th scope="col">Situação</th>,
      celula: (i) => <td><SituacaoDoInstrumento i={i} /></td>,
    },
    {
      chave: 'serie',
      cabeca: <th scope="col" aria-label="Ações" />,
      celula: (i) => (
        <td>
          <button type="button" className="secundario" onClick={() => verSerie(i.id)}>
            Ver a série
          </button>
        </td>
      ),
    },
  ];
}

/** A ficha do instrumento: onde está, como foi instalado e a contagem. */
function fichaDoInstrumento(i: Instrumento, verSerie: (id: string) => void): FichaDaLinha {
  return {
    rotulo: 'Instrumento',
    titulo: i.codigo,
    tituloCodigo: true,
    sub: (
      <>
        <span>{i.tipo_nome}</span>
        <span>{[i.cliente, i.obra_nome].filter(Boolean).join(' · ')}</span>
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos"><SituacaoDoInstrumento i={i} /></div>
        {i.motivo_situacao && (
          <div className="tf-aviso"><strong>{rotulo(i.situacao)}.</strong> {i.motivo_situacao}</div>
        )}
        <CamposDaFicha itens={[
          ['OS · ponto', `${i.os_numero} · ${i.ponto_codigo}`],
          ['Obra', i.obra_nome],
          ['Instalado', `${dia(i.data_instalacao)}${i.instalado_por_nome ? ` · ${i.instalado_por_nome}` : ''}`],
          ['Aparelho', [i.fabricante, i.modelo, i.numero_serie && `série ${i.numero_serie}`]
            .filter(Boolean).join(' · ')],
          ['Profundidade', i.profundidade_instalacao != null ? `${num(i.profundidade_instalacao, 2)} m` : null],
          ['Cota da boca', i.cota_boca != null ? `${num(i.cota_boca, 3)} m` : null],
          ['Leituras', String(i.leituras)],
          ['Última leitura', i.ultima_leitura ? dia(i.ultima_leitura) : null],
        ]} />
      </>
    ),
    acoes: (
      <button type="button" className="botao" onClick={() => verSerie(i.id)}>
        Ver a série
      </button>
    ),
  };
}

export function Instrumentos() {
  const [lista, setLista] = useState<Instrumento[] | null>(null);
  const [pendencias, setPendencias] = useState<Pendencia[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      const [i, p] = await Promise.all([
        verInstrumentos(), instrumentosPendentes()]);
      setLista(i);
      setPendencias(p);
    } catch (e) { setErro(e); setLista([]); }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!lista) return <Carregando texto="Carregando o parque instalado" />;

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Instrumentação</h1>
          <p className="sub">
            O que ficou no chão, e o que ele vem dizendo desde então
          </p>
        </div>
      </div>

      {pendencias.length > 0 && (
        <Aviso tipo="atencao" titulo="O que falta para a série virar gráfico">
          <ul className="lista-simples">
            {pendencias.map((p) => (
              <li key={p.id}>
                <strong>{p.codigo}</strong>
                {p.obra_nome ? ` · ${p.obra_nome}` : ''} — {p.falta}
              </li>
            ))}
          </ul>
        </Aviso>
      )}

      {lista.length === 0 ? (
        <Vazio>
          Nenhum instrumento instalado. Eles nascem no ponto de
          investigação da campanha que os instalou — abra a OS, o ponto, e
          instale a partir dali.
        </Vazio>
      ) : (
        /*
         * O parque instalado numa tabela com ficha (onda 219): o
         * instrumento, o tipo, a última leitura e a situação ficam; obra,
         * instalação e contagem são detalhe e, com a ficha aberta, moram
         * nela. «Ver a série» continua na linha e abre a série, não a ficha.
         */
        <section className="cartao">
          <TabelaComFicha
            linhas={lista}
            chave={(i) => i.id}
            nome={(i) => `Instrumento ${i.codigo}`}
            rotulo="Instrumentos instalados"
            colunas={colunasDoInstrumento(setAberto)}
            ficha={(i) => fichaDoInstrumento(i, setAberto)}
          />
        </section>
      )}

      {aberto && (
        <Serie id={aberto} aoFechar={() => { setAberto(null); void carregar(); }} />
      )}
    </div>
  );
}

// ── a série de um instrumento ────────────────────────────────────────

function Serie({ id, aoFechar }: { id: string; aoFechar(): void }) {
  const { eu } = useSessao();
  const [dados, setDados] = useState<
    { instrumento: Instrumento; leituras: Leitura[] } | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [lancando, setLancando] = useState(false);

  const carregar = useCallback(async () => {
    try { setDados(await verInstrumento(id)); } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  const conferir = async (leituraId: string, ok: boolean) => {
    setRecado(null);
    try {
      if (ok) {
        await conferirLeitura(leituraId, { situacao: 'conferida' });
      } else {
        const motivo = window.prompt(
          'Por que esta leitura foi rejeitada? Ela continua na série, '
          + 'marcada — apagar seria perder a evidência da visita.');
        if (!motivo || motivo.trim().length < 10) return;
        await conferirLeitura(leituraId, {
          situacao: 'rejeitada', motivo: motivo.trim() });
      }
      await carregar();
    } catch (e) { setRecado(mensagemDoErro(e)); }
  };

  const parar = async () => {
    const motivo = window.prompt(
      'O que houve com o instrumento? A série vai ter uma lacuna a '
      + 'partir de hoje, e a lacuna sem explicação é a pergunta que '
      + 'volta do cliente daqui a seis meses.');
    if (!motivo || motivo.trim().length < 10) return;
    setRecado(null);
    try {
      await situacaoDoInstrumento(id,
        { situacao: 'obstruido', motivo: motivo.trim() });
      await carregar();
    } catch (e) { setRecado(mensagemDoErro(e)); }
  };

  if (erro) return <Dialogo aberto titulo="Instrumento" aoFechar={aoFechar}>
    <ErroDaApi erro={erro} />
  </Dialogo>;
  if (!dados) return <Dialogo aberto titulo="Instrumento" aoFechar={aoFechar}>
    <Carregando />
  </Dialogo>;

  const i = dados.instrumento;
  const nivel = i.familia === 'nivel';
  /*
   * Há formulário para esta família?
   *
   * O defeito que só apareceu abrindo a tela: o inclinômetro mostrava
   * «Lançar leitura» como qualquer outro, e o botão levava a uma recusa
   * do banco. Oferecer o que o sistema vai negar é pior do que não
   * oferecer — quem clica conclui que quebrou, e não que ainda não
   * existe. Aqui a ausência do botão vem com a frase que a explica.
   */
  const seLe = i.familia === 'nivel' || i.familia === 'recalque';

  return (
    <Dialogo aberto titulo={`${i.codigo} · ${i.tipo_nome}`} aoFechar={aoFechar}>
      <dl className="resumo-linha">
        <div><dt>Ponto</dt><dd>{i.ponto_codigo}</dd></div>
        <div><dt>Obra</dt><dd>{i.obra_nome ?? '—'}</dd></div>
        <div><dt>OS de instalação</dt><dd>{i.os_numero}</dd></div>
        <div><dt>Instalado em</dt><dd>{dia(i.data_instalacao)}</dd></div>
        <div><dt>Cota da boca</dt><dd>{num(i.cota_boca)}</dd></div>
        <div><dt>Família</dt><dd>{rotulo(i.familia)}</dd></div>
      </dl>

      {i.situacao !== 'ativo' && (
        <Aviso tipo="atencao" titulo={rotulo(i.situacao)}>
          {i.motivo_situacao}
        </Aviso>
      )}

      {i.nivel_sem_cota_boca && (
        <Aviso tipo="info" titulo="Sem cota de boca">
          A série sai em profundidade a partir da boca, e não em cota. É
          leitura válida — só não é o gráfico que o cliente espera.
        </Aviso>
      )}

      {i.recalque_sem_referencia && (
        <Aviso tipo="info" titulo="Sem leitura zero">
          O recalque é contado a partir de uma leitura de referência.
          Enquanto nenhuma estiver marcada, os deslocamentos ficam em
          branco — em vez de contados a partir da leitura mais antiga que
          por acaso existir.
        </Aviso>
      )}

      {recado && <Aviso tipo="erro">{recado}</Aviso>}

      {!seLe && (
        <Aviso tipo="info" titulo="Este tipo ainda não se lê pelo sistema">
          {i.familia === 'perfil'
            ? 'A leitura do inclinômetro não é um número: é um perfil '
              + '(A0/A180/B0/B180 a cada intervalo). Transformá-lo em '
              + 'deslocamento depende do checksum, da tolerância dele e '
              + 'do conjunto de referência — critérios que a Testesolo '
              + 'ainda não definiu, e que o sistema não vai supor. '
              + 'Registre a visita no diário de campo até lá.'
            : 'O instrumento está cadastrado, instalado e contado. O que '
              + 'se lê nele é a Testesolo que diz — e enquanto não '
              + 'disser, um campo genérico «valor» guardaria um número '
              + 'sem grandeza.'}
        </Aviso>
      )}

      <div className="linha-topo">
        <div className="cresce"><h3>A série</h3></div>
        {i.situacao === 'ativo' && (
          <>
            {seLe && (
              <button type="button" onClick={() => setLancando(true)}>
                Lançar leitura
              </button>
            )}
            <button type="button" className="secundario" onClick={parar}>
              Registrar obstrução
            </button>
          </>
        )}
      </div>

      {dados.leituras.length === 0 ? (
        <Vazio>Nenhuma leitura ainda.</Vazio>
      ) : (
        <table className="dados">
          <thead>
            <tr>
              <th>Data</th>
              <th className="num">{nivel ? 'Prof. (m)' : 'Cota (m)'}</th>
              <th className="num">
                {nivel ? 'Cota do nível (m)' : 'Recalque (mm)'}
              </th>
              <th className="num">Variação ({nivel ? 'm' : 'mm'})</th>
              <th>Leu</th>
              <th>Situação</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dados.leituras.map((l) => (
              <tr key={l.id}
                  className={l.situacao === 'rejeitada' ? 'descartada' : undefined}>
                <td>
                  {dia(l.data)}
                  {l.hora && <small className="det"> {l.hora.slice(0, 5)}</small>}
                  {l.referencia && <Selo tom="neutro">Zero</Selo>}
                </td>
                <td className="num">
                  {l.seco ? <em>seco</em>
                    : num(nivel ? l.profundidade_nivel : l.cota_lida,
                          nivel ? 3 : 4)}
                </td>
                <td className="num">
                  {nivel ? num(l.cota_nivel) : num(l.recalque_mm, 2)}
                </td>
                <td className="num">{num(l.variacao, nivel ? 3 : 2)}</td>
                <td>{l.lida_por_nome ?? '—'}</td>
                <td>
                  <Selo tom={TOM[l.situacao] ?? 'neutro'}
                        titulo={l.motivo_rejeicao ?? undefined}>
                    {rotulo(l.situacao)}
                  </Selo>
                </td>
                <td>
                  {l.situacao === 'lancada' && (
                    <>
                      <button type="button" className="secundario"
                              onClick={() => void conferir(l.id, true)}>
                        Conferir
                      </button>
                      <button type="button" className="secundario"
                              onClick={() => void conferir(l.id, false)}>
                        Rejeitar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {lancando && (
        <NovaLeitura
          instrumento={i}
          funcionarioId={eu?.funcionarioId ?? null}
          aoFechar={() => setLancando(false)}
          aoGravar={async () => { setLancando(false); await carregar(); }}
        />
      )}
    </Dialogo>
  );
}

// ── lançar uma leitura ───────────────────────────────────────────────

/**
 * O formulário da FAMÍLIA, e não um formulário para tudo.
 *
 * `nivel` pergunta a profundidade a partir da boca, e tem o botão de
 * «poço seco» — que é uma leitura, e não a falta dela: o poço secar é
 * informação de projeto, e registrar «sem leitura» perderia a diferença
 * entre «a equipe não foi» e «a equipe foi e o poço estava seco».
 *
 * `recalque` pergunta a cota lida no nivelamento, e oferece marcar a
 * leitura zero — uma por instrumento, e só nesta família.
 */
function NovaLeitura({ instrumento, funcionarioId, aoFechar, aoGravar }: {
  instrumento: Instrumento;
  funcionarioId: string | null;
  aoFechar(): void;
  aoGravar(): Promise<void>;
}) {
  const nivel = instrumento.familia === 'nivel';
  const [data, setData] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [hora, setHora] = useState('');
  const [valor, setValor] = useState('');
  const [seco, setSeco] = useState<string | null>(null);
  const [referencia, setReferencia] = useState<string | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [recado, setRecado] = useState<string | null>(null);
  const [guardado, setGuardado] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  const gravar = async () => {
    setRecado(null);
    if (!funcionarioId) {
      setRecado('A sessão não tem funcionário vinculado — quem leu precisa '
        + 'ter nome na leitura.');
      return;
    }
    const n = valor.trim() ? Number(valor.replace(',', '.')) : null;
    setGravando(true);
    try {
      await lancarLeitura(instrumento.id, {
        osId: instrumento.os_id,
        data,
        hora: hora || null,
        lidaPor: funcionarioId,
        seco: nivel ? seco === 'sim' : null,
        profundidadeNivel: nivel && seco !== 'sim' ? n : null,
        cotaLida: nivel ? null : n,
        referencia: referencia === 'sim',
        observacoes: observacoes.trim() || undefined,
      }, `Leitura de ${instrumento.codigo} · ${dia(data)}`);
      await aoGravar();
    } catch (e) {
      /*
       * «Guardado no aparelho» NÃO é erro, e não pode aparecer em
       * vermelho — mas também não pode aparecer como «gravado», porque
       * as duas frases significam coisas diferentes e o técnico volta do
       * campo achando que mandou. É verde com ressalva, e o número no
       * cabeçalho conta a mesma história.
       */
      if (e instanceof FicouNaFila) {
        setGuardado(
          'Gravado neste aparelho. Sobe assim que houver rede — pode '
          + 'continuar lançando.');
        setValor('');
        setSeco(null);
        setObservacoes('');
      } else {
        setRecado(mensagemDoErro(e));
      }
    } finally { setGravando(false); }
  };

  return (
    <Dialogo aberto titulo={`Leitura de ${instrumento.codigo}`} aoFechar={aoFechar}>
      {recado && <Aviso tipo="erro">{recado}</Aviso>}
      {guardado && (
        <Aviso tipo="atencao" titulo="Sem rede">{guardado}</Aviso>
      )}

      <div className="linha">
        <Campo rotulo="Data" type="date" value={data}
               onChange={(e) => setData(e.target.value)} />
        <Campo rotulo="Hora" type="time" value={hora}
               onChange={(e) => setHora(e.target.value)}
               dica="Em branco na leitura de topografia" />
      </div>

      {nivel && (
        <Escolha
          rotulo="O poço estava seco?"
          opcoes={[{ valor: 'sim', texto: 'Sim, seco' },
            { valor: 'nao', texto: 'Não, há nível' }]}
          valor={seco}
          aoEscolher={setSeco}
          dica="Poço seco é uma leitura, e não a falta dela."
        />
      )}

      {(!nivel || seco !== 'sim') && (
        <Campo
          rotulo={nivel ? 'Profundidade do nível, a partir da boca (m)'
            : 'Cota lida no nivelamento (m)'}
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          dica={nivel
            ? `A cota do nível sai daqui: ${num(instrumento.cota_boca)} − esta leitura.`
            : 'A cota que o nivelamento entregou, sem converter para deslocamento.'}
        />
      )}

      {!nivel && (
        <Escolha
          rotulo="É a leitura zero da série?"
          opcoes={[{ valor: 'sim', texto: 'Sim' }, { valor: 'nao', texto: 'Não' }]}
          valor={referencia}
          aoEscolher={setReferencia}
          dica="Uma por instrumento. É dela que o recalque é contado."
        />
      )}

      <Campo rotulo="Observações" value={observacoes}
             onChange={(e) => setObservacoes(e.target.value)} />

      <div className="acoes">
        <button type="button" onClick={() => void gravar()} disabled={gravando}>
          {gravando ? 'Gravando…' : 'Lançar'}
        </button>
        <button type="button" className="secundario" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}
